import { callApi } from '@core/api/apiWrapper'
import type { Paginated } from '@shared/models/types'
import type {
  DiagnosticsCompetenceRemoteData,
  DiagnosticsDashboardStatRemoteData,
  DiagnosticsItemRemoteData,
  DiagnosticsItemStateRemoteData,
  DiagnosticsItemTestRemoteData,
  DiagnosticsItemResultRemoteData,
  RemoteError
} from './types'
import type { CatalogFilters } from '@diagnostics/controllers/DiagnosticsCatalogController'
import { convertCatalogParamsLocalToRemote } from '@diagnostics/helpers/converters'
import type { OrganizationRemoteData } from '@dict/services/types'
import type { TrainerTaskAnswerRemoteData } from '@abstract-trainer/services/types'

export const fetchCompetences = async () => {
  const response = await callApi<DiagnosticsCompetenceRemoteData[]>({
    method: 'get',
    url: 'diagnostic/competences/',
    authRequired: false
  })

  return response
}

export const fetchOrganizations = async () => {
  const response = await callApi<OrganizationRemoteData[]>({
    method: 'get',
    url: 'diagnostic/organizations/',
    authRequired: false
  })

  return response
}

export const fetchDiagnosticsItems = async (
  filters: Partial<CatalogFilters>,
  search: string,
  order: string | null,
  page: number,
  pageSize: number
) => {
  const params = convertCatalogParamsLocalToRemote(filters)
  if (order) params.ordering = order
  if (search) params.search_query = search
  if (pageSize) params.page_size = pageSize
  if (page > 1) params.page = page
  const response = await callApi<Paginated<DiagnosticsItemRemoteData>>({
    method: 'get',
    url: 'diagnostic/',
    params
    // authRequired: false
  })
  return response
}

export const fetchDiagnosticsItem = async (id: number) => {
  const response = await callApi<DiagnosticsItemRemoteData, RemoteError>({
    method: 'get',
    url: `diagnostic/${id}/`
    // authRequired: false
  })

  return response
}

export const fetchDiagnosticsItemState = async (id: number) => {
  const response = await callApi<DiagnosticsItemStateRemoteData>({
    method: 'get',
    url: `diagnostic/${id}/state/`
  })
  return response
}

export const fetchDiagnosticsItemTest = async (id: number) => {
  const response = await callApi<DiagnosticsItemTestRemoteData, RemoteError>({
    method: 'get',
    url: `diagnostic/${id}/stage/`
  })
  return response
}

export const fetchDiagnosticsItemTestAnswers = async (id: number) => {
  const response = await callApi<TrainerTaskAnswerRemoteData[]>({
    method: 'get',
    url: `diagnostic/${id}/answers/`
  })
  return response
}

export const fetchDiagnosticsItemResult = async (id: number) => {
  const response = await callApi<DiagnosticsItemResultRemoteData>({
    method: 'get',
    url: `diagnostic/${id}/result/`
  })
  return response
}

export const startDiagnosticsItemTest = async (id: number) => {
  const response = await callApi<DiagnosticsItemStateRemoteData>({
    method: 'post',
    url: `diagnostic/${id}/start-timer/`
  })
  return response
}

export const finishDiagnosticsItemTest = async (id: number) => {
  const response = await callApi<DiagnosticsItemStateRemoteData>({
    method: 'post',
    url: `diagnostic/${id}/attempt/`
  })
  return response
}

export const retryDiagnosticsItemTest = async (id: number) => {
  const response = await callApi<DiagnosticsItemStateRemoteData>({
    method: 'delete',
    url: `diagnostic/${id}/answers/`
  })
  return response
}

export const getDashboardStats = async () => {
  const response = await callApi<{
    competences: DiagnosticsDashboardStatRemoteData[]
    diagnostic_count: number
  }>({
    method: 'get',
    url: 'diagnostic-dashboard/'
  })
  return response
}
